import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronLeft, CheckCircle, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import { toast } from 'sonner';

export default function AdminProviders() {
  const { api } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchProviders();
  }, [page]);

  const fetchProviders = async () => {
    try {
      const response = await api.get(`/admin/providers?page=${page}&limit=20`);
      setProviders(response.data.providers);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
      toast.error('Erro ao carregar prestadores');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (providerId) => {
    try {
      await api.put(`/admin/providers/${providerId}/verify`);
      toast.success('Prestador verificado');
      fetchProviders();
    } catch (error) {
      toast.error('Erro ao verificar prestador');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-providers">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Prestadores</h1>
          <p className="text-slate-600 mt-1">Gerenciar clínicas e CNPJs</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <div key={`skeleton-${i}`} className="animate-pulse h-40 bg-white rounded-2xl"></div>
            ))
          ) : providers.length > 0 ? (
            providers.map((provider) => (
              <div key={provider.id} className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-slate-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {provider.is_verified && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verificado
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-slate-900">{provider.trade_name}</h3>
                    <p className="text-sm text-slate-600">{provider.legal_name}</p>
                    <p className="text-xs text-slate-500">{provider.cnpj}</p>
                    <p className="text-sm text-slate-500 mt-2">
                      {provider.city}/{provider.state}
                    </p>
                    {provider.rating_avg > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span className="text-sm font-medium">{provider.rating_avg.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                </div>
                {!provider.is_verified && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Button
                      size="sm"
                      className="bg-primary"
                      onClick={() => handleVerify(provider.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Verificar
                    </Button>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="col-span-2 text-center py-16 bg-white rounded-2xl">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhum prestador encontrado</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              Anterior
            </Button>
            <span className="flex items-center px-4 text-sm text-slate-600">
              Página {page} de {totalPages}
            </span>
            <Button variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
              Próxima
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
